import { formatMoney } from '@mms/shared';
import { Badge, Button, Form } from 'react-bootstrap';
import { useFieldArray, type UseFormReturn } from 'react-hook-form';
import { z } from 'zod';
import type { Row } from '../api/types';
import { useCan } from '../auth/AuthContext';
import type { AnyForm } from '../forms/fields';
import { accountNoReq, alphaReq, balanceReq, currencyCodeReq, dateReq, digitsOpt, digitsReq, emailOpt, emailReq, mobileOpt, mobileReq, fxRateReq, selectReq, toId, toIdOrNull, toMoney, toText } from '../forms/zod';
import { Amount, fmtDate } from '../ui/format';
import { toOptions, useBaseCurrency, useLatestRates, useLookup } from '../ui/lookups';
import { useBalances } from '../ui/BalanceCard';
import type { Column } from '../ui/DataTable';
import type { CrudConfig } from './CrudScreen';

const yes = (v: boolean) => (v ? <i className="bi bi-check-lg text-success" aria-label="Yes" /> : <span className="text-body-tertiary" aria-label="No">–</span>);

const refCol: Column = { key: 'referenceNumber', header: 'Ref', className: 'text-nowrap text-body-secondary', hideOnMobile: true };
const currencyCol: Column = { key: 'currencyCode', header: 'Currency', sortKey: 'currency' };

// ---------------------------------------------------------------- customers
// Agents, Sellers and Dispatchers are multi-currency masters (agents.tsx, sellers.tsx, dispatchers.tsx).

// Sellers (purchasers) get their own config, not partyConfig: the list shows a live, computed
// "Outstanding Amount" (the existing Purchaser balance formula - opening + Debit payments - Credit
// payments - purchase amount, i.e. Purchase and Payment only) instead of the static Opening balance,
// and drops the separate Currency column since the balance is now tagged with its own code inline -
// same treatment as Dispatchers below.
// A seller holds a repeatable list of currencies, each with its own opening balance (requested
// 2026-10-03: "abbas ss" can have an OMR balance and an AED balance at once) - a child list, not a
// single column, the same shape an invoice's line items use. The generic field-list form (FieldDef)
// has no repeatable-rows type, so this list is rendered directly via the modal's `aside` slot instead.
type CurrencyRow = { currencyId: string; openingBalance: string };
const currencyRowSchema = z.object({ currencyId: selectReq('Select a currency'), openingBalance: balanceReq });
const currenciesSchema = z
  .array(currencyRowSchema)
  .min(1, 'Add at least one currency')
  .superRefine((rows, ctx) => {
    const seen = new Set<string>();
    rows.forEach((c, i) => {
      if (c.currencyId && seen.has(c.currencyId)) ctx.addIssue({ code: 'custom', path: [i, 'currencyId'], message: 'This currency is already added' });
      seen.add(c.currencyId);
    });
  });

function PartyCurrencyRows({ form, mode, resource }: { form: AnyForm; mode: 'create' | 'edit'; resource: 'purchasers' | 'suppliers' }) {
  const rhf = form as unknown as UseFormReturn<{ currencies: CurrencyRow[] }>;
  const { fields, append, remove } = useFieldArray({ control: rhf.control, name: 'currencies' });
  const canEdit = useCan(resource, mode === 'create' ? 'create' : 'edit');
  const disabled = !canEdit;
  const currencies = useLookup('currencies');
  // Dispatchers (suppliers) only offer currencies flagged "isSupplier", when any are flagged at all.
  const usable = resource === 'suppliers' && currencies.data?.some((c) => c.isSupplier) ? currencies.data.filter((c) => c.isSupplier) : currencies.data;
  const options = toOptions(usable, (c) => `${c.code} – ${c.name}`);
  const err = (i: number, k: keyof CurrencyRow) => (form.formState.errors as never as { currencies?: { [i: number]: { [key: string]: { message?: string } } } })?.currencies?.[i]?.[k]?.message;

  return (
    <div className="mb-3">
      <Form.Label className="d-block">Currencies<span className="text-danger ms-1" aria-hidden>*</span></Form.Label>
      {fields.map((f, i) => (
        <div className="row g-2 mb-2" key={f.id}>
          <div className="col-6 col-md-5">
            <Form.Select size="sm" aria-label={`Currency ${i + 1}`} disabled={disabled} isInvalid={!!err(i, 'currencyId')} {...form.register(`currencies.${i}.currencyId`)}>
              <option value="">Select…</option>
              {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Form.Select>
            <Form.Control.Feedback type="invalid">{err(i, 'currencyId')}</Form.Control.Feedback>
          </div>
          <div className="col-5 col-md-5">
            <Form.Control
              size="sm" inputMode="decimal" autoComplete="off" placeholder="Opening balance" disabled={disabled}
              aria-label={`Currency ${i + 1} opening balance`} isInvalid={!!err(i, 'openingBalance')}
              {...form.register(`currencies.${i}.openingBalance`)}
            />
            <Form.Control.Feedback type="invalid">{err(i, 'openingBalance')}</Form.Control.Feedback>
          </div>
          {!disabled && (
            <div className="col-1 d-flex align-items-start pt-1">
              <Button size="sm" variant="outline-danger" aria-label={`Remove currency ${i + 1}`} disabled={fields.length === 1} onClick={() => remove(i)}>
                <i className="bi bi-x-lg" aria-hidden />
              </Button>
            </div>
          )}
        </div>
      ))}
      {!disabled && (
        <Button size="sm" variant="outline-primary" onClick={() => append({ currencyId: '', openingBalance: '' })}>
          <i className="bi bi-plus-lg me-1" aria-hidden />Add currency
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- customers
export const customersConfig: CrudConfig = {
  title: 'Customers', singular: 'customer', icon: 'people', menu: 'customers', endpoint: '/customers', update: 'patch',
  schema: z.object({ name: alphaReq(), agentId: selectReq('Select an agent'), supplierId: selectReq('Select a supplier') }),
  defaults: () => ({ name: '', agentId: '', supplierId: '' }),
  toForm: (r) => ({ name: r.name, agentId: r.agentId ? String(r.agentId) : '', supplierId: r.supplierId ? String(r.supplierId) : '' }),
  toPayload: (v) => ({ name: v.name.trim(), agentId: toId(v.agentId), supplierId: toIdOrNull(v.supplierId) }),
  rowLabel: (r) => r.name,
  initialSort: { key: 'name', dir: 'asc' },
  useTable: () => {
    const agents = useLookup('agents');
    return {
      columns: [
        refCol,
        { key: 'name', header: 'Customer', sortKey: 'name', className: 'fw-semibold' },
        { key: 'agentName', header: 'Agent', sortKey: 'agent' },
        { key: 'supplierName', header: 'Dispatcher', sortKey: 'supplier', hideOnMobile: true },
        { key: 'currencyCode', header: 'Currency', hideOnMobile: true },
      ],
      filters: [{ param: 'agentId', label: 'Agent', options: toOptions(agents.data) }],
    };
  },
  useForm: () => {
    const agents = useLookup('agents');
    const suppliers = useLookup('suppliers');
    return {
      fields: [
        { name: 'name', label: 'Customer name', type: 'text', required: true, col: 12, help: 'Letters only' },
        { name: 'agentId', label: 'Agent', type: 'select', required: true, options: toOptions(agents.data as Row[]), help: 'The customer trades in the agent’s currency' },
        { name: 'supplierId', label: 'Dispatcher', type: 'select', required: true, options: toOptions(suppliers.data) },
      ],
    };
  },
};
